import {
  IonBadge, IonButton, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle,
  IonCardTitle, IonCheckbox, IonCol, IonContent, IonFooter, IonIcon, IonItem,
  IonLabel, IonModal, IonSpinner, IonText, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { logoWhatsapp } from 'ionicons/icons';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { isAllowedMetaMessageOrigin, loadMetaFacebookSdk } from '../../../../../Utils/MetaFacebookSdk';
import {
  requestService, whatsappMetaActivate, whatsappMetaDisable,
  whatsappMetaOnboardingComplete, whatsappMetaOnboardingSession, whatsappMetaStatus,
  whatsappMetaTest,
} from '../../../../../Utils/Services';

type Props = { empresaId: string };
type MetaStatus = {
  available: boolean;
  status: string;
  activeChannel: 'baileys' | 'meta_cloud_api' | 'none';
  integration?: { displayPhoneNumber?: string; verifiedName?: string; lastError?: { message?: string } };
};
type SignupSession = {
  sessionId: string;
  nonce: string;
  publicConfig: { appId: string; graphApiVersion: string; loginOptions: Record<string, any> };
};
type ActiveFlow = SignupSession & {
  empresaId: string;
  code?: string;
  embeddedSignup?: Record<string, any>;
  completing?: boolean;
  timeoutId?: number;
};

function endpoint(template: string, empresaId: string) {
  return template.replace('{empresaId}', encodeURIComponent(empresaId));
}

const WhatsAppMetaCard: React.FC<Props> = ({ empresaId }) => {
  const history = useHistory();
  const [status, setStatus] = useState<MetaStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [toast, setToast] = useState('');
  const mounted = useRef(true);
  const flow = useRef<ActiveFlow | null>(null);

  const clearFlow = useCallback(() => {
    if (flow.current?.timeoutId) window.clearTimeout(flow.current.timeoutId);
    flow.current = null;
  }, []);

  const api = useCallback(<T,>(url: string, method: string, body?: unknown) => new Promise<T>((resolve, reject) => {
    requestService(url, {
      method,
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }, history, (value: any) => resolve(value as T), reject, true);
  }), [history]);

  const refresh = useCallback(async () => {
    if (!empresaId) {
      setStatus(null);
      return;
    }
    try {
      const next = await api<MetaStatus>(endpoint(whatsappMetaStatus.url, empresaId), whatsappMetaStatus.method);
      if (mounted.current) setStatus(next);
    } catch (_) {
      if (mounted.current) setStatus(null);
    }
  }, [api, empresaId]);

  const completeIfReady = useCallback(async () => {
    const current = flow.current;
    if (!current || current.completing || !current.code || !current.embeddedSignup) return;
    if (current.empresaId !== empresaId) {
      clearFlow();
      setBusy(false);
      setToast('A empresa selecionada mudou. Inicie o cadastro novamente.');
      return;
    }
    current.completing = true;
    try {
      await api(endpoint(whatsappMetaOnboardingComplete.url, empresaId), whatsappMetaOnboardingComplete.method, {
        sessionId: current.sessionId,
        nonce: current.nonce,
        code: current.code,
        embeddedSignup: current.embeddedSignup,
      });
      clearFlow();
      await refresh();
      setConfirmed(false);
      setShowModal(true);
    } catch (error: any) {
      clearFlow();
      setToast(error?.message || 'Não foi possível concluir o Embedded Signup.');
    } finally {
      setBusy(false);
    }
  }, [api, clearFlow, empresaId, refresh]);

  useEffect(() => {
    mounted.current = true;
    clearFlow();
    setBusy(false);
    setShowModal(false);
    setConfirmed(false);
    refresh();
    return () => { mounted.current = false; clearFlow(); };
  }, [clearFlow, refresh]);

  useEffect(() => {
    const listener = (message: MessageEvent) => {
      if (!isAllowedMetaMessageOrigin(message.origin) || !flow.current) return;
      let payload = message.data;
      if (typeof payload === 'string') {
        try { payload = JSON.parse(payload); } catch (_) { return; }
      }
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;
      const eventName = payload.event || payload.data?.event;
      if (eventName === 'CANCEL' || eventName === 'ERROR') {
        clearFlow();
        setBusy(false);
        setToast(eventName === 'CANCEL' ? 'Cadastro cancelado.' : 'A Meta informou uma falha no cadastro.');
        return;
      }
      if (eventName !== 'FINISH' && eventName !== 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') return;
      const data = payload.data || {};
      flow.current.embeddedSignup = {
        ...data,
        wabaId: data.waba_id || data.wabaId,
        phoneNumberId: data.phone_number_id || data.phoneNumberId,
        businessId: data.business_id || data.businessId,
        onboardingType: 'whatsapp_business_app_onboarding',
        featureType: 'whatsapp_business_app_onboarding',
      };
      void completeIfReady();
    };
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
  }, [clearFlow, completeIfReady]);

  const startSignup = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const session = await api<SignupSession>(endpoint(whatsappMetaOnboardingSession.url, empresaId), whatsappMetaOnboardingSession.method, {});
      flow.current = {
        ...session,
        empresaId,
        timeoutId: window.setTimeout(() => {
          clearFlow();
          setBusy(false);
          setToast('O tempo do cadastro expirou. Inicie novamente.');
        }, 5 * 60 * 1000),
      };
      const facebook = await loadMetaFacebookSdk(session.publicConfig.appId, session.publicConfig.graphApiVersion);
      facebook.login((result: any) => {
        if (!flow.current || flow.current.empresaId !== empresaId) return;
        const code = result?.authResponse?.code;
        if (!code) {
          clearFlow();
          setBusy(false);
          setToast('Popup fechado ou autorização cancelada.');
          return;
        }
        flow.current.code = code;
        void completeIfReady();
      }, session.publicConfig.loginOptions);
    } catch (error: any) {
      clearFlow();
      setBusy(false);
      setToast(error?.message || 'Não foi possível iniciar o Embedded Signup.');
    }
  };

  const activate = async () => {
    if (!confirmed || busy) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaActivate.url, empresaId), whatsappMetaActivate.method, { confirmouBaileysDesvinculado: true });
      await refresh();
      setShowModal(false);
      setToast('WhatsApp Oficial ativado.');
    } catch (error: any) {
      setToast(error?.message || 'Não foi possível ativar a integração.');
    } finally { setBusy(false); }
  };

  const testConnection = async () => {
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaTest.url, empresaId), whatsappMetaTest.method, {});
      setToast('WABA, número e token validados com sucesso.');
    } catch (error: any) { setToast(error?.message || 'Falha ao validar a integração.'); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    const accepted = window.confirm('Desconectar a integração oficial do Xeguei? Isso não exclui a conta nem remove o número do WhatsApp Business.');
    if (!accepted) return;
    setBusy(true);
    try {
      await api(endpoint(whatsappMetaDisable.url, empresaId), whatsappMetaDisable.method, {});
      await refresh();
      setShowModal(false);
      setToast('Integração oficial desconectada.');
    } catch (error: any) { setToast(error?.message || 'Não foi possível desconectar a integração.'); }
    finally { setBusy(false); }
  };

  if (!status?.available) return null;
  const connected = status.status === 'connected' && status.activeChannel === 'meta_cloud_api';
  const pending = status.status === 'configuration_pending';

  return <>
    <IonCol size="12" sizeMd="6" sizeLg="6">
      <IonCard style={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <IonCardHeader style={{ position: 'relative' }}>
          {(connected || pending) && <IonBadge color={connected ? 'success' : 'warning'} style={{ position: 'absolute', right: 8, top: 8 }}>
            {connected ? 'Ativo' : 'Configuração pendente'}
          </IonBadge>}
          <IonIcon icon={logoWhatsapp} color="success" style={{ fontSize: 80 }} />
          <IonCardTitle>WhatsApp Oficial</IonCardTitle>
          <IonCardSubtitle>Meta Cloud API</IonCardSubtitle>
        </IonCardHeader>
        <IonCardContent style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <p>{status.integration?.verifiedName || 'Conecte o número atual pelo fluxo de Coexistência.'}</p>
          <IonButton size="large" disabled={busy} onClick={pending || connected ? () => setShowModal(true) : startSignup}>
            {busy ? <IonSpinner name="crescent" /> : pending || connected ? 'Configurar' : 'Ativar'}
          </IonButton>
        </IonCardContent>
      </IonCard>
    </IonCol>

    <IonModal isOpen={showModal} onDidDismiss={() => setShowModal(false)}>
      <IonToolbar color="primary"><IonTitle>WhatsApp Oficial</IonTitle></IonToolbar>
      <IonContent className="ion-padding">
        {pending && <>
          <h2>Última etapa</h2>
          <p>Para evitar respostas duplicadas, desconecte o dispositivo antigo utilizado pelo Xeguei em WhatsApp Business → Aparelhos conectados. Não exclua sua conta e não remova o número do WhatsApp Business.</p>
          <IonItem lines="none">
            <IonCheckbox slot="start" checked={confirmed} onIonChange={(event) => setConfirmed(event.detail.checked)} />
            <IonLabel className="ion-text-wrap">Confirmo que desconectei o dispositivo antigo do Xeguei/Baileys.</IonLabel>
          </IonItem>
          <IonButton expand="block" disabled={!confirmed || busy} onClick={activate}>Confirmar e ativar</IonButton>
        </>}
        {connected && <>
          <h2>Integração ativa</h2>
          <p>{status.integration?.verifiedName} {status.integration?.displayPhoneNumber}</p>
          <IonButton expand="block" disabled={busy} onClick={testConnection}>Validar conexão</IonButton>
          <IonButton expand="block" color="danger" fill="outline" disabled={busy} onClick={disable}>Desconectar do Xeguei</IonButton>
          <IonText color="medium"><p>Desconectar aqui não exclui a conta nem remove o número do WhatsApp Business.</p></IonText>
        </>}
      </IonContent>
      <IonFooter><IonToolbar><IonButton slot="end" fill="clear" onClick={() => setShowModal(false)}>Fechar</IonButton></IonToolbar></IonFooter>
    </IonModal>
    <IonToast isOpen={Boolean(toast)} message={toast} duration={5000} onDidDismiss={() => setToast('')} />
  </>;
};

export default WhatsAppMetaCard;
